import { Component, inject, signal, OnInit, PLATFORM_ID } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { CardModule } from 'primeng/card';
import { ButtonModule } from 'primeng/button';
import { SelectModule } from 'primeng/select';
import { InputTextModule } from 'primeng/inputtext';

import { CoiView } from '../coi-view/coi-view';
import { CoiService } from '../../../services/coi';
import { ToastService } from '../../../services/toast';
import { PageTitleService } from '../../../services/page-title';
import { DropdownItem } from '../../../services/dropdown';

type LookupKey = 'POLICY' | 'MEMBER';

/**
 * Find Certificate — look up a COI by Policy/Certificate No or Member Id, then view / print /
 * download it (via the shared CoiView). Search is button-driven; supports a ?ref= deep link.
 */
@Component({
  selector: 'app-coi-finder',
  standalone: true,
  imports: [CommonModule, FormsModule, CardModule, ButtonModule, SelectModule, InputTextModule, CoiView],
  templateUrl: './coi-finder.html',
  styleUrls: ['./coi-finder.css'],
})
export class CoiFinder implements OnInit {
  private svc = inject(CoiService);
  private toast = inject(ToastService);
  private route = inject(ActivatedRoute);
  private platformId = inject(PLATFORM_ID);
  readonly pageTitle = inject(PageTitleService).title;

  readonly keyOptions: DropdownItem[] = [
    { value: 'POLICY', label: 'Certificate / Policy No' },
    { value: 'MEMBER', label: 'Member Id' },
  ];

  key = signal<LookupKey>('POLICY');
  term = signal('');
  searching = signal(false);
  searched = signal(false);
  /** The found member's id — fed to CoiView (which loads data + the certificate HTML). */
  foundMemberId = signal<number | null>(null);

  ngOnInit() {
    if (!isPlatformBrowser(this.platformId)) return;
    const ref = (this.route.snapshot.queryParamMap.get('ref') || '').trim();
    if (ref) {
      const numeric = /^\d+$/.test(ref);
      this.key.set(numeric ? 'MEMBER' : 'POLICY');
      this.term.set(ref);
      this.find();
    }
  }

  find() {
    const value = this.term().trim();
    if (!value) {
      this.toast.warning('Enter a certificate no or member id to search.');
      return;
    }

    this.searching.set(true);
    this.searched.set(true);
    this.foundMemberId.set(null);

    const onResult = (res: any) => {
      this.searching.set(false);
      const id = Number(res?.data?.memberId) || 0;
      if (res?.success && id > 0) {
        this.foundMemberId.set(id);
      } else {
        this.toast.error(res?.message || 'No certificate found for that reference.');
      }
    };
    const onError = (e: any) => {
      this.searching.set(false);
      this.toast.error(e?.error?.message || 'No certificate found for that reference.');
    };

    if (this.key() === 'MEMBER') {
      const id = Number(value);
      if (!Number.isInteger(id) || id <= 0) {
        this.searching.set(false);
        this.toast.warning('Enter a valid Member Id.');
        return;
      }
      this.svc.coiData(id).subscribe({ next: onResult, error: onError });
    } else {
      this.svc.coiDataByPolicy(value).subscribe({ next: onResult, error: onError });
    }
  }

  clear() {
    this.term.set('');
    this.foundMemberId.set(null);
    this.searched.set(false);
  }

  get placeholder(): string {
    return this.key() === 'MEMBER' ? 'e.g. 123' : 'e.g. POORNA-2026-000123';
  }
}
